'use client';

import type { ConversationalSearchFeedback, FeedbackRating } from 'conversational-search-client';
import { createContext, useCallback, useContext, useMemo, useReducer, type JSX, type ReactNode } from 'react';

import { recordFeedbackRating, submitFeedbackComment } from '../_actions/feedbackActions';
import { useChatSessionContext } from './SessionContext';

export type FeedbackState = 'prompt' | 'comment' | 'confirmation' | 'dismissed';

interface FeedbackReducerState {
    state: FeedbackState;
    feedback: ConversationalSearchFeedback | null;
    error: string | null;
    isPending: boolean;
}

type Action =
    | { type: 'rating-pending' }
    | { type: 'rating-saved'; row: ConversationalSearchFeedback }
    | { type: 'rating-failed'; message: string }
    | { type: 'comment-pending' }
    | { type: 'comment-saved'; row: ConversationalSearchFeedback }
    | { type: 'comment-failed'; message: string }
    | { type: 'dismiss' };

function reducer(state: FeedbackReducerState, action: Action): FeedbackReducerState {
    switch (action.type) {
        case 'rating-pending':
            return { ...state, isPending: true, error: null };
        // Both ratings open the comment box. A thumbs-down asks what went wrong;
        // a thumbs-up asks what went well, because knowing why an experience was
        // good tells us how to position this feature against keyword search.
        case 'rating-saved':
            return {
                state: 'comment',
                feedback: action.row,
                error: null,
                isPending: false,
            };
        case 'rating-failed':
            return { ...state, isPending: false, error: action.message };
        case 'comment-pending':
            return { ...state, isPending: true, error: null };
        case 'comment-saved':
            return { state: 'confirmation', feedback: action.row, error: null, isPending: false };
        case 'comment-failed':
            return { ...state, isPending: false, error: action.message };
        case 'dismiss':
            return { ...state, state: 'dismissed' };
        default:
            return state;
    }
}

export interface FeedbackContextValue {
    state: FeedbackState;
    feedback: ConversationalSearchFeedback | null;
    error: string | null;
    isPending: boolean;
    userMessageThreshold: number;
    recordRating: (rating: FeedbackRating, conversationMessageId: string) => Promise<void>;
    submitComment: (comment: string, commentMessageId: string) => Promise<void>;
    dismiss: () => void;
}

const FeedbackContext = createContext<FeedbackContextValue | undefined>(undefined);

export function useFeedbackContext(): FeedbackContextValue {
    const ctx = useContext(FeedbackContext);
    if (!ctx) {
        throw new Error('useFeedbackContext must be used within a FeedbackProvider');
    }
    return ctx;
}

interface FeedbackProviderProps {
    children: ReactNode;
    initialFeedback: ConversationalSearchFeedback | null;
    lookupSucceeded: boolean;
    enabled: boolean;
    userMessageThreshold: number;
}

function deriveInitialState(
    initialFeedback: ConversationalSearchFeedback | null,
    lookupSucceeded: boolean,
    enabled: boolean,
): FeedbackReducerState {
    const dismissed: FeedbackReducerState = {
        state: 'dismissed',
        feedback: initialFeedback,
        error: null,
        isPending: false,
    };
    if (!enabled) return dismissed;
    if (!lookupSucceeded) return dismissed;
    if (initialFeedback) return dismissed;
    return { state: 'prompt', feedback: null, error: null, isPending: false };
}

export function FeedbackProvider({
    children,
    initialFeedback,
    lookupSucceeded,
    enabled,
    userMessageThreshold,
}: FeedbackProviderProps): JSX.Element {
    // Read sessionId reactively from SessionContext. On a fresh chat, the
    // server-rendered URL is '/new' so the page-level sessionId param is "new"
    // — the real conversation id is only assigned client-side after the first
    // stream completes (see useNewChatSession in SessionContext). Reading it
    // from context here picks up that update without remounting this provider.
    const { sessionId } = useChatSessionContext();

    const [reducerState, dispatch] = useReducer(reducer, undefined, () =>
        deriveInitialState(initialFeedback, lookupSucceeded, enabled),
    );

    const recordRating = useCallback(
        async (rating: FeedbackRating, conversationMessageId: string): Promise<void> => {
            if (!enabled || !sessionId) return;
            dispatch({ type: 'rating-pending' });
            try {
                const row = await recordFeedbackRating({ sessionId, conversationMessageId, rating });
                dispatch({ type: 'rating-saved', row });
            } catch {
                dispatch({
                    type: 'rating-failed',
                    message: "Couldn't save your rating. Please try again.",
                });
            }
        },
        [enabled, sessionId],
    );

    const submitComment = useCallback(
        async (comment: string, commentMessageId: string): Promise<void> => {
            if (!enabled) return;
            if (reducerState.state !== 'comment' || !reducerState.feedback) return;
            const feedbackId = reducerState.feedback.id;
            dispatch({ type: 'comment-pending' });
            try {
                const row = await submitFeedbackComment({ feedbackId, comment, commentMessageId });
                dispatch({ type: 'comment-saved', row });
            } catch {
                dispatch({
                    type: 'comment-failed',
                    message: "Couldn't save your comment. Please try again.",
                });
            }
        },
        [enabled, reducerState.state, reducerState.feedback],
    );

    const dismiss = useCallback(() => dispatch({ type: 'dismiss' }), []);

    const value = useMemo<FeedbackContextValue>(
        () => ({
            state: reducerState.state,
            feedback: reducerState.feedback,
            error: reducerState.error,
            isPending: reducerState.isPending,
            userMessageThreshold,
            recordRating,
            submitComment,
            dismiss,
        }),
        [reducerState, userMessageThreshold, recordRating, submitComment, dismiss],
    );

    return <FeedbackContext value={value}>{children}</FeedbackContext>;
}
